import React from "react";
import { Box, Typography, Container, Grid, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import ComplianceIcon from "@icons/ComplianceIcon";
import ChartIcon from "@icons/ChartIcon";
import PolicyIcon from "@icons/PolicyIcon";
import RegulationsIcon from "@assets/Law.png";
import TrainingIcon from "@assets/Training.png";
import Auditor from "@assets/Shield Person.png";
import {
  AUDITOR_DESC,
  AUDITOR_TITLE,
  AVIATION_AUDITOR_DESC,
  AVIATION_AUDITOR_TITLE,
  AVIATION_COMPLIANCE_DESC,
  AVIATION_COMPLIANCE_TITLE,
  AVIATION_PENALTY_DESC,
  AVIATION_PENALTY_TITLE,
  AVIATION_POLICY_DESC,
  AVIATION_POLICY_TITLE,
  AVIATION_REGULATION_DESC,
  AVIATION_REGULATION_TITLE,
  AVIATION_TRAINING_DESC,
  AVIATION_TRAINING_TITLE,
  COMPLIANCE_DESC,
  COMPLIANCE_TITLE,
  FEATURE_TITLE,
  FEATURE_TITLE1,
  FEATURE_TITLE2,
  PENALTY_DESC,
  PENALTY_TITLE,
  POLICY_DESC,
  POLICY_TITLE,
  REGULATION_DESC,
  REGULATION_TITLE,
  TRAINING_DESC,
  TRAINING_TITLE,
} from "@constants/unicomply-keys";

export type LandingVertical = "healthcare" | "aviation";

const FeaturesSection = styled(Box)(({ theme }) => ({
  padding: "100px 0",
  backgroundColor: "#f8fafb",
  position: "relative",
  [theme.breakpoints.down("md")]: {
    padding: "60px 0",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "40px 0",
  },
}));

const FeatureCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "highlighted",
})<{ highlighted?: boolean }>(
  ({ highlighted = false, theme }) => ({
    display: "flex",
    height: "200px",
    padding: "37.5px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "15px",
    width: "100%",
    maxWidth: "350px",
    [theme.breakpoints.down("md")]: {
      height: "180px",
      padding: "25px",
      gap: "12px",
    },
    [theme.breakpoints.down("sm")]: {
      height: "160px",
      padding: "20px",
      gap: "10px",
      maxWidth: "100%",
    },
    ...(highlighted
      ? {
          borderRadius: "22.5px",
          border: "1px solid #C9F9FD",
          background: "#F2FDFE",
        }
      : {
          borderRadius: "22.5px",
          border: "1px solid rgba(0,59,62,1)",
          background: "#fff",
        }),
  })
);

const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "highlighted",
})<{ highlighted?: boolean }>(
  ({ highlighted, theme }) => ({
    width: 60,
    height: 60,
    backgroundColor: highlighted ? "none" : "#fcfcfc",
    borderRadius: 9.375,
    boxShadow: highlighted
      ? "none"
      : "6px 6.75px 26.25px rgba(26, 26, 26, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      width: 50,
      height: 50,
    },
  })
);

const FeatureIcon = styled(Box)({
  width: 30,
  height: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    width: "100%",
    height: "100%",
    fill: "#0a829d",
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "contain", // keeps PNG ratio
  },
});

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  highlighted?: boolean;
}

function FeatureCardComponent({
  title,
  description,
  icon,
  highlighted = false,
}: FeatureCardProps) {
  return (
    <FeatureCard highlighted={highlighted}>
      {/* Icon */}
      <IconContainer highlighted={highlighted}>
        <FeatureIcon>{icon}</FeatureIcon>
      </IconContainer>

      {/* Content */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9375, flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            fontFamily: "Rethink Sans",
            color: "#1a1a1a",
            fontSize: { xs: 18, sm: 20, md: 24 },
            lineHeight: "normal",
            width: "100%",
            maxWidth: { xs: "100%", md: 247.5 },
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 400,
            fontFamily: "Rethink Sans",
            color: "#787878",
            fontSize: { xs: 13, sm: 14, md: 15 },
            lineHeight: "normal",
            width: "100%",
            maxWidth: { xs: "100%", md: 196.5 },
          }}
        >
          {description}
        </Typography>
      </Box>
    </FeatureCard>
  );
}

export default function UniComply_Features({
  vertical = "healthcare",
}: {
  vertical?: LandingVertical;
}) {
  const isAviation = vertical === "aviation";
  const features = [
    {
      title: isAviation ? AVIATION_COMPLIANCE_TITLE : COMPLIANCE_TITLE,
      description: isAviation ? AVIATION_COMPLIANCE_DESC : COMPLIANCE_DESC,
      icon: <ComplianceIcon />,
      highlighted: false,
    },
    {
      title: isAviation ? AVIATION_REGULATION_TITLE : REGULATION_TITLE,
      description: isAviation ? AVIATION_REGULATION_DESC : REGULATION_DESC,
      icon: (
        <img
          src={RegulationsIcon}
          alt="Regulations"
          style={{ width: "60px", height: "60px" }}
        />
      ),
      highlighted: true,
    },
    {
      title: isAviation ? AVIATION_PENALTY_TITLE : PENALTY_TITLE,
      description: isAviation ? AVIATION_PENALTY_DESC : PENALTY_DESC,
      icon: <ChartIcon />,
      highlighted: false,
    },
    {
      title: isAviation ? AVIATION_TRAINING_TITLE : TRAINING_TITLE,
      description: isAviation ? AVIATION_TRAINING_DESC : TRAINING_DESC,
      icon: (
        <img
          src={TrainingIcon}
          alt="Regulations"
          style={{ width: "60px", height: "60px" }}
        />
      ),
      highlighted: true,
    },
    {
      title: isAviation ? AVIATION_POLICY_TITLE : POLICY_TITLE,
      description: isAviation ? AVIATION_POLICY_DESC : POLICY_DESC,
      icon: <PolicyIcon />,
      highlighted: false,
    },
    {
      title: isAviation ? AVIATION_AUDITOR_TITLE : AUDITOR_TITLE,
      description: isAviation ? AVIATION_AUDITOR_DESC : AUDITOR_DESC,
      icon: (
        <img
          src={Auditor}
          alt="Auditor"
          style={{ width: "33px", height: "33px" }}
        />
      ),
      highlighted: true,
    },
  ];

  return (
    <FeaturesSection id="features">
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: "center", marginBottom: { xs: 4, md: 8 }, px: { xs: 2, md: 0 } }}>
          <Typography
            sx={{
              fontSize: { xs: 24, sm: 30, md: 37.5 },
              fontWeight: 700,
              color: "#1a1a1a",
            }}
          >
            {FEATURE_TITLE}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 24, sm: 30, md: 37.5 },
              fontWeight: 700,
              color: "#1a1a1a",
              marginBottom: 2,
            }}
          >
            <Box component="span">{FEATURE_TITLE1}</Box>
            <Box
              component="span"
              sx={{ color: "var(--Brand-Colors-color-brand-primary, #0A829D)" }}
            >
              {FEATURE_TITLE2}
            </Box>
          </Typography>
        </Box>

        {/* Features Grid */}
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          justifyContent="center"
          alignItems="stretch"
        >
          {features.map((feature, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={index}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <FeatureCardComponent
                title={feature.title}
                description={feature.description}
                icon={feature.icon}
                highlighted={feature.highlighted}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </FeaturesSection>
  );
}
